import type { ToolId } from './catalogue';

/**
 * "Open this picture tool on this picture" from outside the Edit tab — the Generate panel's
 * suggestion when the words ask to change the selected picture ("remove the background",
 * "make it bigger"; Jev `artwork.generate.tool`). The editor page opens the Edit tab; the
 * picture panel opens the tool's row, with the words already in it for a tool that takes words.
 * A request waits for the panel when it is not on screen yet.
 */
export interface PictureToolRequest { id: string; tool: ToolId; words?: string }

/** How long a request waits for its panel to mount (the Edit tab opening). */
const WAIT_MS = 3000;

let pending: { request: PictureToolRequest; at: number } | null = null;
const listeners = new Set<(request: PictureToolRequest) => void>();

export function requestPictureTool(request: PictureToolRequest): void {
  pending = { request, at: Date.now() };
  for (const listener of [...listeners]) listener(request);
}

export function onPictureToolRequest(listener: (request: PictureToolRequest) => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * The fresh request for this picture, if any. Read, not used up: React runs a mounting panel's
 * effects twice in development, and a request taken by the first run was gone for the second.
 */
export function pictureToolRequestFor(id: string, now = Date.now()): PictureToolRequest | null {
  return pending && pending.request.id === id && now - pending.at <= WAIT_MS ? pending.request : null;
}
